import type { GraphItem, GraphQuestion } from './graph';
import type { Answer, PackageNodeItemOption, QuestionOptionId } from './types';

/**
 * Hoe een antwoord gelezen wordt. Deze drie regels bepalen samen de traversal;
 * ze zijn 1-op-1 overgenomen uit `graph-semantics.md` §2.1.
 */

/**
 * Is de vraag beantwoord? Een lege string en een lege selectie tellen als
 * onbeantwoord — precies zoals het legacy `antwoord != null && antwoord != ""`.
 */
export function isAnswered(question: GraphQuestion, answer: Answer | undefined): boolean {
    if (!answer) {
        return false;
    }

    switch (question.question.type) {
        case 'options':
        case 'yes_no':
        case 'options_multi':
        case 'options_single_multi':
            return (answer.option_ids?.length ?? 0) > 0;
        case 'number':
            return (
                answer.value_number !== null &&
                answer.value_number !== undefined &&
                Number.isFinite(answer.value_number)
            );
        case 'text':
            return typeof answer.value_text === 'string' && answer.value_text !== '';
        case 'text_items':
            return (
                (answer.value_items?.length ?? 0) > 0 ||
                (typeof answer.value_text === 'string' && answer.value_text !== '')
            );
        default:
            return false;
    }
}

/**
 * Is dit een aantal van nul — "hier is niets"?
 *
 * Elke `number`-vraag in de vragenlijst vraagt een hoeveelheid: hoeveel stuks,
 * hoeveel m², welk bedrag. Nul is daarop geen hoeveelheid maar een ontkenning,
 * en dus geen antwoord waarop iets volgt. Onbeantwoord is iets anders: daar
 * stopt de wandeling, hier loopt hij door naar de volgende tak.
 */
export function isZeroQuantity(question: GraphQuestion, answer: Answer | undefined): boolean {
    return question.question.type === 'number' && answer?.value_number === 0;
}

/**
 * De randen die gevolgd worden, **altijd in de volgorde van de randen van het
 * item** — niet in de volgorde waarin de inspecteur aankruiste.
 *
 * Waardetypes (`number`, `text`, `text_items`) hebben geen betekenisvolle
 * optie-id en volgen onvoorwaardelijk alle randen van het item. Op één na: een
 * aantal van nul volgt geen enkele rand, zodat de vervolgvragen eronder met hun
 * oordelen en kosten vervallen (`isZeroQuantity`).
 */
export function selectedOptions(
    question: GraphQuestion,
    item: GraphItem,
    answer: Answer | undefined,
): readonly PackageNodeItemOption[] {
    if (isZeroQuantity(question, answer)) {
        return [];
    }

    if (question.isValueType) {
        return item.options;
    }

    const selected = answer?.option_ids;

    if (!selected || selected.length === 0) {
        return [];
    }

    const wanted = new Set<QuestionOptionId>(selected);

    return item.options.filter((option) => wanted.has(option.question_option_id));
}

/**
 * De hoeveelheidsvermenigvuldiger voor de kosten: staat de rand op een
 * `number`-vraag, dan is het bedrag een eenheidsprijs maal het ingevulde getal
 * — het exacte getal, ook met decimalen (12,5 m blijft 12,5; legacy kapte af).
 * Alles anders telt als 1×. Afgerond wordt pas het bedrag, zie `multiplyCents`.
 */
export function costQuantity(question: GraphQuestion, answer: Answer | undefined): number {
    if (question.question.type !== 'number') {
        return 1;
    }

    const value = answer?.value_number;

    if (value === null || value === undefined || !Number.isFinite(value)) {
        return 1;
    }

    return value;
}
